import type { InsurerFormEntry } from '@cyberscore/contracts';
import { FileSignature, Upload } from 'lucide-react';
import { useRef } from 'react';
import { can, useSession } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { useInsurerCatalogue, useUploadTemplate } from './api';

const MAX_BYTES = 10 * 1024 * 1024;

/**
 * The insurers' blank proposal forms (task 92, decision 0069). The platform fills each insurer's own PDF,
 * mapped against one edition of it; the organisation uploads that blank form once.
 * `embedded`: inside Tenant 360's tab (task 102), under a smaller heading.
 */
export function InsurerFormsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const forms = useInsurerCatalogue();
  const { tenant } = useSession();
  const uploads = can(tenant, 'intake.create');

  return (
    <>
      <div className={`page-head${embedded ? ' embedded' : ''}`}>
        {embedded ? <h2>Insurer forms</h2> : <h1>Insurer forms</h1>}
        <p>
          The insurers’ own proposal forms, filled from each client’s answers. Upload each insurer’s blank
          form once: it must be the edition the form was mapped on.
        </p>
      </div>
      <section className="card">
        {forms.isPending ? (
          <div className="empty">Loading…</div>
        ) : forms.isError ? (
          <div className="empty">
            <strong>Could not load the forms</strong>
            {forms.error.message}
          </div>
        ) : !forms.data.length ? (
          <div className="empty">
            <FileSignature size={28} />
            <strong>No insurer forms yet</strong>
            The platform publishes the insurers’ forms it maps.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>Form</th>
                  <th className="wide">Blank form</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {forms.data.map((f) => (
                  <Row key={f.key} f={f} uploads={uploads} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function Row({ f, uploads }: { f: InsurerFormEntry; uploads: boolean }) {
  const upload = useUploadTemplate();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const t = f.template;
  const matches = !!t && !!f.pdf && t.sha256 === f.pdf.sha256;
  const pick = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_BYTES) return toast('A blank form is at most 10 MB', 'bad');
    upload.mutate(
      { key: f.key, file },
      {
        onSuccess: (r) => toast(r.sha256 === f.pdf?.sha256 ? `${f.insurer}’s form is ready to fill` : `Uploaded, but it isn’t the edition of ${f.form_code} that was mapped`, r.sha256 === f.pdf?.sha256 ? 'ok' : 'bad'), // prettier-ignore
        onError: (e) => toast(e.message, 'bad'),
      },
    );
  };
  return (
    <tr>
      <td>
        <b>{f.insurer}</b>
        <div className="muted small">
          {f.product} · <span className="mono">{f.form_code}</span>
        </div>
      </td>
      <td className="wide small">
        {!f.pdf ? (
          <span className="muted">Not mapped to the insurer’s PDF yet</span>
        ) : !t ? (
          <>
            <span className="pill neutral">Not uploaded</span>
            {f.source_document && <div className="muted">Expects {f.source_document}</div>}
          </>
        ) : (
          <>
            <span className={`pill ${matches ? 'ok' : 'bad'}`}>
              {matches ? 'Ready to fill' : 'Another edition'}
            </span>
            <div className="muted">
              {t.file_name} · uploaded {new Date(t.uploaded_at).toLocaleDateString('en-MY')}
              {t.uploaded_by_name && ` by ${t.uploaded_by_name}`}
            </div>
          </>
        )}
      </td>
      <td className="right nowrap">
        {uploads && f.pdf && (
          <>
            <input
              ref={input}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              aria-label={`Blank form for ${f.insurer} ${f.form_code}`}
              onChange={(e) => {
                pick(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="btn btn-sm"
              disabled={upload.isPending}
              onClick={() => input.current?.click()}
            >
              <Upload size={14} /> {upload.isPending ? 'Uploading…' : t ? 'Replace' : 'Upload'}
            </button>
          </>
        )}
      </td>
    </tr>
  );
}
